import { Link } from 'react-router-dom';

export default function SEOSection() {
  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-4xl mx-auto">
        {/* What is GTBuy Spreadsheet */}
        <article className="mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
            What Is a GTBuy Spreadsheet and Why You Need One
          </h2>
          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 space-y-4 leading-relaxed">
            <p>
              A <strong>GTBuy spreadsheet</strong> is a powerful shopping agent comparison tool designed to help online shoppers make smarter purchasing decisions.
              Whether you are buying sneakers, fashion apparel, electronics, or accessories from international markets, having a structured comparison
              system ensures you never overpay for shipping, fees, or product markups.
            </p>
            <p>
              The core idea behind the <strong>GTBuy spreadsheet system</strong> is simple: collect and organize data from multiple shopping agents &mdash;
              including <strong>GTBuy</strong>, <strong>Kakobuy</strong>, and <strong>Superbuy</strong> &mdash; into one easy-to-read comparison table.
              This lets you instantly see which agent offers the lowest total cost, fastest shipping, and best customer trust rating for your specific product category.
            </p>
            <p>
              Unlike traditional price comparison websites that only compare product prices across retailers, a <strong>shopping agent comparison spreadsheet</strong>
              compares the entire fulfillment chain: agent service fees, international shipping rates, package consolidation costs, insurance options, and
              real user trust scores. This holistic approach saves shoppers an average of <strong>15&ndash;40% per order</strong> compared to using
              a single agent without comparison.
            </p>
          </div>
        </article>

        {/* Comparison Table */}
        <article className="mb-12">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
            GTBuy vs Kakobuy vs Superbuy &mdash; Quick Comparison
          </h3>
          <div className="overflow-x-auto rounded-lg border border-background-200/70">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-secondary-100">
                  <th className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Platform</th>
                  <th className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Service Fee</th>
                  <th className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Shipping Speed</th>
                  <th className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Trust Score</th>
                  <th className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Best For</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                <tr className="hover:bg-background-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-primary-600 whitespace-nowrap">GTBuy</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">Low (3&ndash;5%)</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">7&ndash;14 days</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-accent-700 font-semibold">
                      <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i> 9.5
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">Smart buyers</td>
                </tr>
                <tr className="hover:bg-background-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground-800 whitespace-nowrap">Kakobuy</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">Medium (5&ndash;8%)</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">10&ndash;18 days</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-accent-700 font-semibold">
                      <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i> 8.8
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">Fashion</td>
                </tr>
                <tr className="hover:bg-background-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground-800 whitespace-nowrap">Superbuy</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">Higher (8&ndash;12%)</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">5&ndash;10 days</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-accent-700 font-semibold">
                      <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i> 9.2
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">Premium items</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-foreground-500">
            * Data updated for 2026. Actual fees and shipping times may vary by destination and product category.
          </p>
        </article>

        {/* Why GTBuy Spreadsheet */}
        <article className="mb-12">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
            Why Smart Shoppers Use a GTBuy Spreadsheet
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white rounded-lg p-5 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                <i className="ri-money-dollar-circle-line w-5 h-5 flex items-center justify-center text-accent-700"></i>
              </div>
              <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">Save Money</h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Compare service fees and shipping costs across multiple agents to find the cheapest total price.
                Our spreadsheet system highlights the best value option for every product category.
              </p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                <i className="ri-time-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
              </div>
              <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">Save Time</h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                No more manually checking each agent website. One spreadsheet gives you all the data
                you need &mdash; pricing, shipping ETAs, and trust ratings &mdash; in a single organized view.
              </p>
            </div>
            <div className="bg-white rounded-lg p-5 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center text-secondary-700"></i>
              </div>
              <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">Shop Safely</h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Every agent in our comparison is rated by real users. Avoid unreliable agents
                and only shop with platforms that have verified trust scores above 8.5.
              </p>
            </div>
          </div>
        </article>

        {/* CTA to Article Page */}
        <div className="text-center py-8">
          <Link
            to="/what-is-gtbuy-spreadsheet"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-8 py-3.5 rounded-md text-sm md:text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-text-line w-5 h-5 flex items-center justify-center"></i>
            Read Our Complete Guide to GTBuy Spreadsheet
          </Link>
          <p className="mt-3 text-xs text-foreground-500">
            In-depth analysis, tips, and strategies for getting the best deals with shopping agent spreadsheets.
          </p>
        </div>

        {/* Featured Article Preview */}
        <article className="bg-white rounded-lg p-6 md:p-8 border border-background-200/70">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="flex-1">
              <span className="inline-block bg-accent-100 text-accent-800 text-xs font-medium px-2.5 py-1 rounded-full mb-3">
                Featured Article
              </span>
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-2">
                The Complete Beginner&rsquo;s Guide to Shopping Agent Spreadsheets
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Learn how to use spreadsheets to compare shopping agents, understand fee structures,
                calculate total landed costs, and discover which agent offers the best value for your specific needs.
                Perfect for first-time international shoppers looking to save money on sneakers, clothing, and electronics.
              </p>
              <Link
                to="/what-is-gtbuy-spreadsheet"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Read Full Article
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="w-full md:w-48 h-36 flex-shrink-0 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20arrangement%20with%20an%20open%20laptop%20displaying%20a%20vibrant%20organized%20spreadsheet%20with%20color%20coded%20cells%2C%20a%20warm%20ceramic%20coffee%20mug%20and%20a%20small%20succulent%20plant%20nearby%2C%20soft%20natural%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20cozy%20home%20office%20aesthetic%2C%20editorial%20lifestyle%20photography%2C%20warm%20neutral%20tones&width=400&height=300&seq=gtbuy-guide-preview-v2&orientation=landscape"
                alt="GTBuy Spreadsheet Guide - Beginner's tutorial on shopping agent comparison"
                title="Complete Beginner's Guide to Shopping Agent Spreadsheets"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}